import React, { useState, useEffect } from 'react';
import { app } from './firebase';
import { getFirestore, collection, query, getDocs, updateDoc, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const db = getFirestore(app);
const storage = getStorage(app);

export default function TimesheetsView({ isReadOnly, activeContracts = [], consultantDb }) {
  // --- STATE ---
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filter State
  const today = new Date();
  let prevMonthIdx = today.getMonth() - 1;
  let prevYearInit = today.getFullYear();
  if (prevMonthIdx < 0) { prevMonthIdx = 11; prevYearInit -= 1; }
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  
  const [viewMonth, setViewMonth] = useState(months[prevMonthIdx]);
  const [viewYear, setViewYear] = useState(prevYearInit.toString());
  const [selectedConsultant, setSelectedConsultant] = useState('');
  const [activeTab, setActiveTab] = useState('Current'); // 'Current', 'ToHandle', 'Archived'

  // Alerts State
  const [missingSubmissions, setMissingSubmissions] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  
  const [formData, setFormData] = useState({
      consultantId: '',
      month: new Date().toLocaleString('default', { month: 'long' }),
      year: new Date().getFullYear().toString(),
      days: {},
      file: null,
      existingProofUrl: null
  });
  const [uploading, setUploading] = useState(false);

  const years = [2024, 2025, 2026, 2027];

  // --- INITIAL LOAD ---
  useEffect(() => {
      fetchSubmissions();
  }, []);

  // --- ANALYSIS EFFECTS ---
  useEffect(() => {
      if(loading) return;

      // 1. Calculate Missing Submissions (Based on Selected View Period)
      const targetMonthIdx = months.indexOf(viewMonth);
      const targetYearInt = parseInt(viewYear);
      
      const missing = activeContracts.filter(c => {
          if (c.status !== 'Active') return false;
          
          // Check if contract was active during the selected month
          const start = new Date(c.startDate);
          const end = new Date(c.endDate);
          const periodStart = new Date(targetYearInt, targetMonthIdx, 1);
          const periodEnd = new Date(targetYearInt, targetMonthIdx + 1, 0);
          
          if (start > periodEnd || end < periodStart) return false;

          // Check if a timesheet exists for this specific consultant, month, and year
          const hasTimesheet = allSubmissions.some(t => 
              t.consultantId === c.consultantId && 
              t.month === viewMonth && 
              t.year === viewYear
          );
          
          return !hasTimesheet;
      });

      setMissingSubmissions(missing.map(c => ({
          ...c,
          period: `${viewMonth} ${viewYear}`
      })));

      // 2. Pending Approvals (Status is Submitted) - Global or Filtered? 
      // Usually "Pending" is a global todo list, but let's filter by view period to match context
      const pending = allSubmissions.filter(t => t.status === 'Submitted' && t.month === viewMonth && t.year === viewYear);
      setPendingApprovals(pending);

  }, [allSubmissions, activeContracts, loading, viewMonth, viewYear]);

  // Filter Logic
  useEffect(() => {
      let data = allSubmissions;
      
      // Filter by Period
      data = data.filter(t => t.month === viewMonth && t.year === viewYear);

      // Filter by Consultant
      if (selectedConsultant) {
          data = data.filter(t => t.consultantId === selectedConsultant);
      }
      setFilteredSubmissions(data);
  }, [selectedConsultant, allSubmissions, viewMonth, viewYear]);

  const fetchSubmissions = async () => {
      setLoading(true);
      try {
          const q = query(collection(db, "timesheets")); 
          const snap = await getDocs(q);
          const data = snap.docs.map(d => ({id: d.id, ...d.data()}));
          // Sort by date (newest update first)
          const sorted = data.sort((a,b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
          setAllSubmissions(sorted);
      } catch (err) {
          console.error("Error fetching timesheets:", err);
      } finally {
          setLoading(false);
      }
  };

  // --- HELPER: DETERMINE CATEGORY ---
  const getTimesheetCategory = (t) => {
      if (t.status === 'Approved') return 'Archived';
      if (t.status !== 'Approved') return 'ToHandle';
      return 'Current'; // Fallback
  };

  // --- ACTIONS (SUPER ADMIN ONLY) ---

  const handleStatusChange = async (id, newStatus) => {
      if(isReadOnly) return alert("Permission Denied: Only Super Admins can modify timesheets.");
      
      try {
          await updateDoc(doc(db, "timesheets", id), { 
              status: newStatus,
              updatedAt: new Date().toISOString()
          });
          // Optimistic update
          setAllSubmissions(prev => prev.map(t => t.id === id ? { ...t, status: newStatus } : t));
          alert(`Status changed to ${newStatus}`);
      } catch (err) {
          console.error(err);
          alert("Error updating status.");
      }
  };

  const handleDeleteTimesheet = async (id) => {
      if(isReadOnly) return alert("Permission Denied: Only Super Admins can delete timesheets.");
      if(!window.confirm("Permanently delete this timesheet? This cannot be undone.")) return;

      try {
          await deleteDoc(doc(db, "timesheets", id));
          setAllSubmissions(prev => prev.filter(t => t.id !== id));
      } catch (err) {
          console.error(err);
          alert("Error deleting timesheet.");
      }
  };

  const handleDeleteFile = async (t) => {
      if(isReadOnly) return alert("Permission Denied: Only Super Admins can remove files.");
      if(!window.confirm("Remove the proof of work file?")) return;

      try {
          await updateDoc(doc(db, "timesheets", t.id), {
              proofUrl: null
          });
          
          setAllSubmissions(prev => prev.map(item => item.id === t.id ? { ...item, proofUrl: null } : item));
          alert("File removed.");
      } catch (err) {
          console.error(err);
          alert("Error removing file.");
      }
  };

  // --- MODAL HANDLERS ---

  const openNewModal = () => {
      setIsEditing(false);
      setEditId(null);
      setFormData({
          consultantId: '',
          month: viewMonth, // Default to current view
          year: viewYear,
          days: {},
          file: null,
          existingProofUrl: null
      });
      setShowModal(true);
  };

  const openEditModal = (t) => {
      if(isReadOnly) return alert("Permission Denied: Only Super Admins can edit timesheets.");
      
      setIsEditing(true);
      setEditId(t.id);
      setFormData({
          consultantId: t.consultantId,
          month: t.month,
          year: t.year,
          days: t.days || {},
          file: null,
          existingProofUrl: t.proofUrl
      });
      setShowModal(true);
  };

  const handleManualSubmit = async (e) => {
      e.preventDefault();
      if(isReadOnly) return;
      if(!formData.consultantId) return alert("Please select a consultant.");
      
      setUploading(true);
      try {
          const totalHours = Object.values(formData.days).reduce((a, b) => a + Number(b), 0);
          
          // Get Consultant Details
          const contract = activeContracts.find(c => c.consultantId === formData.consultantId);
          const consultantName = contract ? contract.consultantName : 'Unknown Consultant';
          const clientName = contract ? contract.client : '';
          const customerId = contract ? contract.customerId : '';

          // Upload File if new one selected
          let fileUrl = formData.existingProofUrl;
          if(formData.file) {
              const storageRef = ref(storage, `portal_proofs/${formData.year}/${formData.month}/${formData.consultantId}_manual_${formData.file.name}`);
              await uploadBytes(storageRef, formData.file);
              fileUrl = await getDownloadURL(storageRef);
          }

          const payload = {
              consultantId: formData.consultantId,
              consultantName: consultantName,
              client: clientName,
              customerId: customerId,
              month: formData.month,
              year: formData.year,
              days: formData.days,
              totalHours,
              proofUrl: fileUrl || null,
              updatedAt: new Date().toISOString(),
              source: isEditing ? 'Modified by Admin' : 'Manual Entry (Admin)'
          };

          // If creating new, set initial status. If editing, keep status (user changes it via dropdown)
          if (!isEditing) {
              payload.status = 'Submitted';
          }

          const docId = isEditing ? editId : `${formData.year}_${formData.month}_${formData.consultantId}`;
          
          await setDoc(doc(db, "timesheets", docId), payload, { merge: true });

          alert(isEditing ? "Timesheet updated." : "Timesheet created.");
          setShowModal(false);
          fetchSubmissions();

      } catch (err) {
          console.error(err);
          alert("Error: " + err.message);
      } finally {
          setUploading(false);
      }
  };

  const handleDayChange = (day, hours) => {
      setFormData(prev => ({
          ...prev, 
          days: { ...prev.days, [day]: hours }
      }));
  };

  const quickFillWeekdays = () => {
      const daysInMonth = new Date(formData.year, months.indexOf(formData.month) + 1, 0).getDate();
      const newDays = {};
      for(let d = 1; d <= daysInMonth; d++) {
          const date = new Date(formData.year, months.indexOf(formData.month), d);
          const dayOfWeek = date.getDay(); 
          if(dayOfWeek !== 0 && dayOfWeek !== 6) newDays[d] = 8;
      }
      setFormData(prev => ({ ...prev, days: newDays }));
  };

  const renderDayInputs = () => {
      const monthIdx = months.indexOf(formData.month);
      const daysInMonth = new Date(formData.year, monthIdx + 1, 0).getDate();
      return Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => (
          <div key={d} className="col-4 col-md-2 mb-2">
              <div className="input-group input-group-sm">
                  <span className="input-group-text text-muted-theme bg-input border-theme" style={{width: '35px', padding: '0 5px'}}>{d}</span>
                  <input 
                      type="number" 
                      className="form-control" 
                      value={formData.days[d] || ''}
                      onChange={(e) => handleDayChange(d, e.target.value)}
                  />
              </div>
          </div>
      ));
  };

  const billableConsultants = activeContracts.filter(c => c.status === 'Active');

  // --- FILTERED LISTS ---
  const toHandleList = filteredSubmissions.filter(t => getTimesheetCategory(t) === 'ToHandle');
  const archivedList = filteredSubmissions.filter(t => getTimesheetCategory(t) === 'Archived');

  const displayList = activeTab === 'ToHandle' ? toHandleList : archivedList;

  return (
    <div className="container-fluid p-0">
      
      {/* ALERTS SECTION */}
      <div className="row g-4 mb-4">
          {/* MISSING SUBMISSIONS ALERT */}
          <div className="col-md-6">
              <div className="card bg-card border-danger shadow-sm h-100">
                  <div className="card-header border-danger bg-danger bg-opacity-10">
                      <h6 className="mb-0 text-danger fw-bold">
                          <i className="bi bi-exclamation-triangle-fill me-2"></i> 
                          Missing Submissions ({viewMonth} {viewYear})
                      </h6>
                  </div>
                  <div className="card-body p-0">
                      <ul className="list-group list-group-flush">
                          {missingSubmissions.length > 0 ? missingSubmissions.map(c => (
                              <li key={c.id} className="list-group-item bg-transparent border-danger d-flex justify-content-between align-items-center">
                                  <div>
                                      <div className="fw-bold text-main">{c.consultantName}</div>
                                      <small className="text-muted-theme">{c.client}</small>
                                  </div>
                                  <button className="btn btn-sm btn-outline-danger" onClick={() => alert(`Please contact ${c.consultantName} or their SPOC.`)}>
                                      Chase
                                  </button>
                              </li>
                          )) : (
                              <div className="p-3 text-muted-theme small text-center">
                                  All active consultants have submitted for this period.
                              </div>
                          )}
                      </ul>
                  </div>
              </div>
          </div>

          {/* PENDING APPROVALS ALERT */}
          <div className="col-md-6">
              <div className="card bg-card border-warning shadow-sm h-100">
                  <div className="card-header border-warning bg-warning bg-opacity-10">
                      <h6 className="mb-0 text-warning fw-bold">
                          <i className="bi bi-hourglass-split me-2"></i> 
                          Pending Client Approvals ({viewMonth})
                      </h6>
                  </div>
                  <div className="card-body p-0">
                      <ul className="list-group list-group-flush">
                          {pendingApprovals.length > 0 ? pendingApprovals.slice(0, 5).map(t => (
                              <li key={t.id} className="list-group-item bg-transparent border-warning d-flex justify-content-between align-items-center">
                                  <div>
                                      <div className="fw-bold text-main">{t.consultantName}</div>
                                      <small className="text-muted-theme">{t.month} {t.year} ({t.totalHours}h)</small>
                                  </div>
                                  <span className="badge bg-warning text-dark">Submitted</span>
                              </li>
                          )) : (
                              <div className="p-3 text-muted-theme small text-center">
                                  No pending approvals for this period.
                              </div>
                          )}
                          {pendingApprovals.length > 5 && (
                              <div className="p-2 text-center small text-muted-theme">
                                  +{pendingApprovals.length - 5} more...
                              </div>
                          )}
                      </ul>
                  </div>
              </div>
          </div>
      </div>

      {/* HEADER & FILTERS */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
          <div>
            <h2 className="fw-bold text-main">Timesheet Management</h2>
            <p className="text-muted-theme">Review, approve, and manage consultant timesheets.</p>
          </div>
          
          <div className="d-flex gap-2 align-items-center flex-wrap">
              {/* Period Selectors */}
              <select className="form-select w-auto" value={viewMonth} onChange={e => setViewMonth(e.target.value)}>
                  {months.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select className="form-select w-auto" value={viewYear} onChange={e => setViewYear(e.target.value)}>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>

              {/* Consultant Filter */}
              <select className="form-select w-auto" value={selectedConsultant} onChange={e => setSelectedConsultant(e.target.value)}>
                  <option value="">All Consultants</option>
                  {[...new Set([...activeContracts.map(c=>c.consultantId), ...allSubmissions.map(t=>t.consultantId)])].map(id => {
                      const name = activeContracts.find(c=>c.consultantId===id)?.consultantName || allSubmissions.find(t=>t.consultantId===id)?.consultantName || 'Unknown';
                      return <option key={id} value={id}>{name}</option>
                  })}
              </select>

              {!isReadOnly && (
                  <button className="btn btn-primary" onClick={openNewModal}>
                      <i className="bi bi-plus-lg me-2"></i> New Entry
                  </button>
              )}
              <button className="btn btn-outline-primary" onClick={fetchSubmissions} title="Refresh">
                  <i className="bi bi-arrow-clockwise"></i>
              </button>
          </div>
      </div>

      {/* TABS */}
      <ul className="nav nav-tabs border-theme mb-4">
          <li className="nav-item">
              <button 
                  className={`nav-link ${activeTab === 'ToHandle' ? 'active bg-card text-danger fw-bold border-theme border-bottom-0' : 'text-muted-theme border-transparent'}`} 
                  onClick={() => setActiveTab('ToHandle')}
              >
                  To Handle <span className="badge bg-danger ms-1">{toHandleList.length}</span>
              </button>
          </li>
          <li className="nav-item">
              <button 
                  className={`nav-link ${activeTab === 'Archived' ? 'active bg-card text-main border-theme border-bottom-0' : 'text-muted-theme border-transparent'}`} 
                  onClick={() => setActiveTab('Archived')}
              >
                  Archived <span className="badge bg-secondary ms-1">{archivedList.length}</span>
              </button>
          </li>
      </ul>

      {/* CONTENT GRID */}
      {loading ? <div className="text-main">Loading submissions...</div> : (
          <div className="row g-4">
              {displayList.map(t => (
                  <div key={t.id} className="col-lg-6 col-xl-4">
                      <div className={`card h-100 bg-card border-theme shadow-sm ${t.status === 'Approved' ? 'border-success' : (t.status === 'Rejected' ? 'border-danger' : 'border-warning')}`}>
                          <div className="card-header bg-transparent border-theme d-flex justify-content-between align-items-center">
                              <h5 className="mb-0 text-main fw-bold">{t.consultantName}</h5>
                              
                              {/* STATUS DROPDOWN (SUPER ADMIN ONLY) */}
                              {!isReadOnly ? (
                                  <select 
                                      className={`form-select form-select-sm w-auto py-0 ${t.status==='Approved'?'text-success':(t.status==='Rejected'?'text-danger':'text-warning')}`} 
                                      value={t.status}
                                      onChange={(e) => handleStatusChange(t.id, e.target.value)}
                                      style={{height: '30px', fontSize: '0.85rem', fontWeight: 'bold'}}
                                  >
                                      <option>Draft</option>
                                      <option>Submitted</option>
                                      <option>Approved</option>
                                      <option>Rejected</option>
                                  </select>
                              ) : (
                                  <span className={`badge ${t.status === 'Approved' ? 'bg-success' : 'bg-warning text-dark'}`}>{t.status}</span>
                              )}
                          </div>
                          <div className="card-body d-flex flex-column">
                              <div className="mb-3">
                                  <div className="d-flex justify-content-between mb-1">
                                      <span className="text-muted-theme">Period:</span>
                                      <span className="text-main fw-bold">{t.month} {t.year}</span>
                                  </div>
                                  <div className="d-flex justify-content-between">
                                      <span className="text-muted-theme">Total Hours:</span>
                                      <span className="text-main fw-bold">{t.totalHours}</span>
                                  </div>
                                  {t.source && <small className="text-muted-theme d-block mt-1 fst-italic">Source: {t.source}</small>}
                              </div>
                              
                              {/* PROOF FILE SECTION */}
                              <div className="p-3 bg-input border border-theme rounded mb-3 position-relative">
                                  <h6 className="text-primary small fw-bold mb-2">Proof of Work</h6>
                                  {t.proofUrl ? (
                                      <div className="d-flex align-items-center gap-2">
                                          <a href={t.proofUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary flex-grow-1 text-truncate">
                                              <i className="bi bi-file-earmark-pdf text-danger me-2"></i> View File
                                          </a>
                                          {!isReadOnly && (
                                              <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteFile(t)} title="Delete File">
                                                  <i className="bi bi-trash"></i>
                                              </button>
                                          )}
                                      </div>
                                  ) : (
                                      <span className="text-muted small fst-italic">No confirmation file uploaded.</span>
                                  )}
                              </div>

                              <div className="mt-auto pt-3 border-top border-theme d-flex justify-content-between">
                                  {!isReadOnly && (
                                      <>
                                          <button className="btn btn-sm btn-outline-info" onClick={() => openEditModal(t)}>
                                              <i className="bi bi-pencil me-1"></i> Edit
                                          </button>
                                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteTimesheet(t.id)}>
                                              <i className="bi bi-trash me-1"></i> Delete
                                          </button>
                                      </>
                                  )}
                              </div>
                          </div>
                      </div>
                  </div>
              ))}
              
              {displayList.length === 0 && (
                  <div className="col-12 text-center py-5">
                      <div className="text-muted-theme mb-2"><i className="bi bi-inbox fs-1"></i></div>
                      <p className="text-muted-theme">No timesheets found for {viewMonth} {viewYear}.</p>
                  </div>
              )}
          </div>
      )}

      {/* EDIT/CREATE MODAL */}
      {showModal && (
          <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
              <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                  <div className="modal-content">
                      <div className="modal-header border-theme">
                          <h5 className="modal-title text-main">{isEditing ? 'Edit Timesheet' : 'New Manual Entry'}</h5>
                          <button className="btn-close" onClick={() => setShowModal(false)}></button>
                      </div>
                      <div className="modal-body">
                          <form onSubmit={handleManualSubmit}>
                              <div className="row g-3 mb-4">
                                  <div className="col-md-6">
                                      <label className="form-label small text-muted-theme">Consultant</label>
                                      {isEditing ? (
                                          <input className="form-control" disabled value={activeContracts.find(c => c.consultantId === formData.consultantId)?.consultantName || formData.consultantId} />
                                      ) : (
                                          <select 
                                              className="form-select" 
                                              required 
                                              value={formData.consultantId} 
                                              onChange={e => setFormData({...formData, consultantId: e.target.value})}
                                          >
                                              <option value="">Select Consultant...</option>
                                              {billableConsultants.map(c => (
                                                  <option key={c.consultantId} value={c.consultantId}>
                                                      {c.consultantName} ({c.client})
                                                  </option>
                                              ))}
                                          </select>
                                      )}
                                  </div>
                                  <div className="col-md-3">
                                      <label className="form-label small text-muted-theme">Month</label>
                                      <select className="form-select" value={formData.month} onChange={e => setFormData({...formData, month: e.target.value})} disabled={isEditing}>
                                          {months.map(m => <option key={m}>{m}</option>)}
                                      </select>
                                  </div>
                                  <div className="col-md-3">
                                      <label className="form-label small text-muted-theme">Year</label>
                                      <select className="form-select" value={formData.year} onChange={e => setFormData({...formData, year: e.target.value})} disabled={isEditing}>
                                          {years.map(y => <option key={y}>{y}</option>)}
                                      </select>
                                  </div>
                              </div>

                              <div className="mb-3 d-flex justify-content-between align-items-center">
                                  <h6 className="text-main mb-0">Daily Hours</h6>
                                  <button type="button" className="btn btn-sm btn-outline-info" onClick={quickFillWeekdays}>
                                      Quick Fill (8h Mon-Fri)
                                  </button>
                              </div>

                              <div className="p-3 bg-input border border-theme rounded mb-3">
                                  <div className="row g-0">
                                      {renderDayInputs()}
                                  </div>
                                  <div className="text-end mt-2 pt-2 border-top border-theme">
                                      <span className="text-main fw-bold">Total: {Object.values(formData.days).reduce((a, b) => a + Number(b), 0)} Hours</span>
                                  </div>
                              </div>

                              <div className="mb-4">
                                  <label className="form-label small text-muted-theme">Upload New Confirmation (PDF)</label>
                                  <input type="file" className="form-control" accept="application/pdf,image/*" onChange={e => setFormData({...formData, file: e.target.files[0]})} />
                                  {formData.existingProofUrl && (
                                      <small className="text-success d-block mt-1">
                                          <i className="bi bi-check-circle me-1"></i> Current file attached. Uploading new one replaces it.
                                      </small>
                                  )}
                              </div>

                              <div className="modal-footer border-theme px-0 pb-0">
                                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                  <button type="submit" className="btn btn-success" disabled={uploading}>
                                      {uploading ? 'Saving...' : 'Save Changes'}
                                  </button>
                              </div>
                          </form>
                      </div>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
}